import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { createTemplate } from "../../services/rubricService";
import { formatApiError } from "../../utils/helpers/apiErrors";
import ModalShell from "./ModalShell";
import { LocalizedStack } from "./LocalizedFields";
import { emptyText, hasText, trimText } from "./templateTree";

// Create a school template: name (+ optional description) and a starting
// point — blank, or a full copy of any published version the school can see.
export default function NewTemplateModal({ templates, onClose, onCreated }) {
  const { t } = useTranslation();
  // Both are per-language from the start. Only two fields, so all three
  // languages fit at once and the author never has to discover a control to
  // find out that Kazakh and Russian exist.
  const [name, setName] = useState(emptyText);
  const [description, setDescription] = useState(emptyText);
  const [mode, setMode] = useState("copy");
  const [sourceVersion, setSourceVersion] = useState("");
  const [nameError, setNameError] = useState(false);
  const [busy, setBusy] = useState(false);

  const sourceOptions = useMemo(
    () =>
      (templates || []).flatMap((tpl) =>
        (tpl.versions || [])
          .filter((v) => v.status === "published")
          .map((v) => ({
            uuid: v.uuid,
            label: `${tpl.name} — ${t("templates.versionN", { n: v.version_number })}`,
          })),
      ),
    [templates, t],
  );

  useEffect(() => {
    if (sourceOptions.length === 0) {
      setMode("blank");
    } else if (!sourceVersion) {
      setSourceVersion(sourceOptions[0].uuid);
    }
  }, [sourceOptions, sourceVersion]);

  const submit = async () => {
    // One language is enough — the backend falls back kk -> ru -> en.
    if (!hasText(name)) {
      setNameError(true);
      return;
    }
    setBusy(true);
    try {
      const draft = await createTemplate({
        body: {
          name: trimText(name),
          description: trimText(description),
          source_version: mode === "copy" ? sourceVersion : null,
        },
      });
      onCreated(draft);
    } catch (err) {
      toast.error(formatApiError(err, t("templates.createFailed")));
      setBusy(false);
    }
  };

  const startChoices = [
    {
      value: "copy",
      label: t("templates.startCopy"),
      hint: t("templates.startCopyHint"),
      disabled: sourceOptions.length === 0,
    },
    {
      value: "blank",
      label: t("templates.startBlank"),
      hint: t("templates.startBlankHint"),
      disabled: false,
    },
  ];

  return (
    <ModalShell onClose={onClose} busy={busy} maxWidth="max-w-lg">
      <h3 className="text-lg font-bold text-slate-900 pr-8">{t("templates.newTemplateTitle")}</h3>

        <div className="mt-5 space-y-4">
          <div>
            <LocalizedStack
              label={t("templates.nameLabel")}
              value={name}
              autoFocus
              error={nameError}
              placeholder={t("templates.namePlaceholder")}
              onChange={(next) => {
                setName(next);
                if (nameError) setNameError(false);
              }}
            />
            {nameError && (
              <p className="mt-1 text-xs font-semibold text-rose-600">
                {t("templates.nameRequired")}
              </p>
            )}
          </div>

          <LocalizedStack
            label={t("templates.descriptionLabel")}
            value={description}
            onChange={setDescription}
          />

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
              {t("templates.startFromLabel")}
            </label>
            <div className="space-y-2">
              {startChoices.map((choice) => (
                <label
                  key={choice.value}
                  className={`flex items-start gap-3 p-3 rounded-xl border transition ${
                    choice.disabled
                      ? "opacity-50 cursor-not-allowed border-slate-200"
                      : mode === choice.value
                      ? "border-indigo-500 bg-indigo-50/50 cursor-pointer"
                      : "border-slate-200 hover:border-indigo-200 cursor-pointer"
                  }`}
                >
                  <input
                    type="radio"
                    name="start-from"
                    value={choice.value}
                    checked={mode === choice.value}
                    disabled={choice.disabled}
                    onChange={() => setMode(choice.value)}
                    className="mt-0.5 accent-indigo-600"
                  />
                  <span>
                    <span className="block text-sm font-bold text-slate-800">{choice.label}</span>
                    <span className="block text-xs text-slate-500 mt-0.5">{choice.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {mode === "copy" && sourceOptions.length > 0 && (
              <select
                value={sourceVersion}
                onChange={(e) => setSourceVersion(e.target.value)}
                className="mt-2 w-full appearance-none pl-3 pr-10 py-2.5 text-sm font-semibold text-slate-700 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition cursor-pointer"
              >
                {sourceOptions.map((opt) => (
                  <option key={opt.uuid} value={opt.uuid}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {busy ? t("templates.creating") : t("templates.create")}
          </button>
        </div>
    </ModalShell>
  );
}
